import { delayBy } from "@/fp";
import { computed, ref, Ref } from "@vue/composition-api";

export type NotificationStatus = "in-progress" | "done" | "failed";

interface BaseNotification {
  group: string;
  id: string;
  name: string;
  icon: string;
}

export interface SimpleNotification extends BaseNotification {
  type: "simple";
}

export interface SimpleActionNotification extends BaseNotification {
  type: "simple-action";
  actions: Array<{
    icon: string;
    name: string;
    action: (notification: PollNotification) => void;
  }>;
  status?: NotificationStatus;
}

export interface PollNotification extends BaseNotification {
  type: "poll";
  status: NotificationStatus;
  canBeRemovedBeforeFinish: boolean;
  retryCount: number;
  extraProperties?: any;
  actions: Array<{
    icon: string;
    name: string;
    action: (notification: PollNotification) => void;
  }>;
  exec: (notification: PollNotification) => Promise<PollNotification>;
  onCancel?: (notification: PollNotification) => void;
  onRetryLimit?: (notification: PollNotification) => void;
  onDone: (notification: PollNotification) => void;
}

type Notification =
  | SimpleNotification
  | SimpleActionNotification
  | PollNotification;

export const NotificationsManager = (() => {
  let isRunningTasks = false;

  const notifications: Ref<Notification[]> = ref([]);
  const retries: Record<string, number> = {};

  const shouldRunMoreTasks = computed(() =>
    notifications.value.some(
      (n) => n.type === "poll" && n.status === "in-progress"
    )
  );

  const addSimple = (notification: SimpleNotification) => {
    if (notifications.value.find((n) => n.id === notification.id)) {
      notifications.value = notifications.value.map((n) => {
        if (n.id === notification.id) {
          return notification;
        }
        return n;
      });
    } else {
      notifications.value.push(notification);
    }
  };

  const addSimpleAction = (notification: SimpleActionNotification) => {
    if (notifications.value.find((n) => n.id === notification.id)) {
      notifications.value = notifications.value.map((n) => {
        if (n.id === notification.id) {
          return notification;
        }
        return n;
      });
    } else {
      notifications.value.push(notification);
    }
  };

  const addPollTask = (notification: PollNotification) => {
    notifications.value.push(notification);
    if (!isRunningTasks) {
      isRunningTasks = true;
      runPollTasks();
    }
  };

  const remove = (id: string) => {
    notifications.value = notifications.value.filter((n) => n.id !== id);
  };

  const cancel = (id: string) => {
    const notification = notifications.value.find(
      (n) => n.id === id
    ) as Notification;
    if (notification.type === "poll") {
      notification.onCancel && notification.onCancel(notification);
    }
    remove(id);
  };

  const update = (notification: Notification) => {
    notifications.value = notifications.value.map((n) => {
      if (n.id === notification.id) {
        return notification;
      }
      return n;
    });
  };

  const runPollTasks = async () => {
    while (shouldRunMoreTasks.value) {
      await delayBy(3000);
      const pollNotifications = notifications.value.filter(
        (n) => n.type === "poll" && n.status === "in-progress"
      ) as PollNotification[];

      for (const notif of pollNotifications) {
        try {
          const shouldStopRetry =
            retries[notif.id] && retries[notif.id] === notif.retryCount;
          if (shouldStopRetry) {
            notif.onRetryLimit && notif.onRetryLimit(notif);
            notif.status = "failed";
            update(notif);
          } else {
            const updatedNotification: PollNotification = await notif.exec({
              ...notif,
            });
            update(updatedNotification);
            if (updatedNotification.status === "done") {
              notif.onDone(notif);
            }
          }
        } catch (e) {
          if (retries[notif.id]) {
            retries[notif.id] = retries[notif.id] + 1;
          } else {
            retries[notif.id] = 1;
          }
        }
      }
    }
    isRunningTasks = false;
  };
  return {
    addSimple,
    addSimpleAction,
    addPollTask,
    shouldRunMoreTasks,
    cancel,
    remove,
    notifications,
    update,
  };
})();

// @ts-ignore
window.NotificationsManager = NotificationsManager;
